/**
 * 設定モーダル。
 *  ・BGM音量 / 効果音音量
 *  ・ガイド線の表示・非表示
 *  ・振動演出の表示・非表示
 *  ・進化一覧の表示・非表示
 * 変更は即時に反映され、localStorage へ保存される。
 */

import { GameState } from '../core/GameState';
import type { GameContext } from '../game/GameContext';
import type { GameSettings } from '../game/config/gameConfig';
import { BOWL_SKINS } from '../game/config/bowlSkins';
import { unlockedSkinIds } from '../meta/skins';
import { canRecordHighlights } from './HighlightRecorder';
import { streamerModeChange } from './streamerMode';
import { applyMotionPreference, applyTextSize } from './inputMode';

export class SettingsModal {
  private readonly context: GameContext;
  private readonly modal: HTMLElement | null;
  private readonly bgmInput: HTMLInputElement | null;
  private readonly seInput: HTMLInputElement | null;
  private readonly bgmValue: HTMLElement | null;
  private readonly seValue: HTMLElement | null;
  private readonly guideToggle: HTMLInputElement | null;
  private readonly shakeToggle: HTMLInputElement | null;
  private readonly evolutionToggle: HTMLInputElement | null;
  private readonly largeTextToggle: HTMLInputElement | null;
  private readonly highlightToggle: HTMLInputElement | null;
  private readonly reduceMotionToggle: HTMLInputElement | null;
  private readonly tourResetBtn: HTMLButtonElement | null;
  private readonly streamerToggle: HTMLInputElement | null;
  private readonly usageStatsToggle: HTMLInputElement | null;
  private resumeOnClose = false;

  constructor(context: GameContext, root: ParentNode = document) {
    this.context = context;
    this.modal = root.querySelector<HTMLElement>('[data-ui="settings-modal"]');
    this.bgmInput = root.querySelector<HTMLInputElement>('[data-ui="bgm-volume"]');
    this.seInput = root.querySelector<HTMLInputElement>('[data-ui="se-volume"]');
    this.bgmValue = root.querySelector<HTMLElement>('[data-ui="bgm-value"]');
    this.seValue = root.querySelector<HTMLElement>('[data-ui="se-value"]');
    this.guideToggle = root.querySelector<HTMLInputElement>('[data-ui="guide-toggle"]');
    this.shakeToggle = root.querySelector<HTMLInputElement>('[data-ui="shake-toggle"]');
    this.evolutionToggle = root.querySelector<HTMLInputElement>('[data-ui="evolution-toggle"]');
    this.largeTextToggle = root.querySelector<HTMLInputElement>('[data-ui="large-text-toggle"]');
    this.highlightToggle = root.querySelector<HTMLInputElement>('[data-ui="highlight-toggle"]');
    this.reduceMotionToggle = root.querySelector<HTMLInputElement>('[data-ui="reduce-motion-toggle"]');
    // 録画できないブラウザでは項目ごと隠す
    if (!canRecordHighlights()) {
      root.querySelector<HTMLElement>('[data-ui="highlight-field"]')?.setAttribute('hidden', '');
    }
    this.tourResetBtn = root.querySelector<HTMLButtonElement>('[data-ui="tour-reset"]');
    this.streamerToggle = root.querySelector<HTMLInputElement>('[data-ui="streamer-toggle"]');
    this.usageStatsToggle = root.querySelector<HTMLInputElement>('[data-ui="usage-stats-toggle"]');

    this.syncInputs();
    this.bind(root);
    this.showStorageNote(root);
  }

  private syncInputs(): void {
    const settings = this.context.settings;
    if (this.bgmInput) this.bgmInput.value = String(Math.round(settings.bgmVolume * 100));
    if (this.seInput) this.seInput.value = String(Math.round(settings.seVolume * 100));
    if (this.bgmValue) this.bgmValue.textContent = `${Math.round(settings.bgmVolume * 100)}%`;
    if (this.seValue) this.seValue.textContent = `${Math.round(settings.seVolume * 100)}%`;
    if (this.guideToggle) this.guideToggle.checked = settings.showGuide;
    if (this.shakeToggle) this.shakeToggle.checked = settings.showShake;
    if (this.evolutionToggle) this.evolutionToggle.checked = settings.showEvolutionList;
    if (this.largeTextToggle) this.largeTextToggle.checked = settings.largeText;
    if (this.highlightToggle) this.highlightToggle.checked = settings.recordHighlights;
    if (this.reduceMotionToggle) this.reduceMotionToggle.checked = settings.reduceMotion;
    if (this.streamerToggle) this.streamerToggle.checked = settings.streamerMode;
    if (this.usageStatsToggle) this.usageStatsToggle.checked = settings.sendUsageStats;
  }

  private bind(root: ParentNode): void {
    root.querySelector<HTMLButtonElement>('[data-ui="settings"]')?.addEventListener('click', () => {
      this.open();
    });
    root
      .querySelector<HTMLButtonElement>('[data-ui="settings-close"]')
      ?.addEventListener('click', () => this.close());
    root
      .querySelector<HTMLButtonElement>('[data-ui="settings-done"]')
      ?.addEventListener('click', () => this.close());
    root
      .querySelector<HTMLElement>('[data-ui="settings-backdrop"]')
      ?.addEventListener('click', () => this.close());

    this.bgmInput?.addEventListener('input', () => {
      const value = Number(this.bgmInput!.value) / 100;
      if (this.bgmValue) this.bgmValue.textContent = `${Math.round(value * 100)}%`;
      this.update({ bgmVolume: value });
    });
    this.seInput?.addEventListener('input', () => {
      const value = Number(this.seInput!.value) / 100;
      if (this.seValue) this.seValue.textContent = `${Math.round(value * 100)}%`;
      this.update({ seVolume: value });
    });
    this.guideToggle?.addEventListener('change', () => {
      this.update({ showGuide: this.guideToggle!.checked });
    });
    this.shakeToggle?.addEventListener('change', () => {
      this.update({ showShake: this.shakeToggle!.checked });
    });
    this.evolutionToggle?.addEventListener('change', () => {
      this.update({ showEvolutionList: this.evolutionToggle!.checked });
    });
    this.largeTextToggle?.addEventListener('change', () => {
      this.update({ largeText: this.largeTextToggle!.checked });
      applyTextSize(this.largeTextToggle!.checked);
    });
    this.highlightToggle?.addEventListener('change', () => {
      this.update({ recordHighlights: this.highlightToggle!.checked });
    });
    this.reduceMotionToggle?.addEventListener('change', () => {
      this.update({ reduceMotion: this.reduceMotionToggle!.checked });
      applyMotionPreference(this.reduceMotionToggle!.checked);
    });
    this.streamerToggle?.addEventListener('change', () => {
      // 配信モードをオンにしたら名場面の録画もオンにする（録画はあとで個別にオフにできる）
      this.update(streamerModeChange(this.streamerToggle!.checked, canRecordHighlights()));
      if (this.highlightToggle) this.highlightToggle.checked = this.context.settings.recordHighlights;
    });
    this.usageStatsToggle?.addEventListener('change', () => {
      this.update({ sendUsageStats: this.usageStatsToggle!.checked });
    });

    this.tourResetBtn?.addEventListener('click', () => {
      this.context.storage.resetTourElapsedMs();
      this.context.bus.emit('tour:reset');
    });

    document.addEventListener('keydown', (event) => {
      if (event.key === 'Escape' && this.isOpen) {
        event.preventDefault();
        this.close();
      }
    });
  }

  private showStorageNote(root: ParentNode): void {
    const note = root.querySelector<HTMLElement>('[data-ui="storage-note"]');
    if (!note) return;
    note.textContent = this.context.storage.available
      ? ''
      : 'この環境では設定・ハイスコアを保存できません（ゲームは通常どおり遊べます）。';
  }

  get isOpen(): boolean {
    return this.modal?.dataset.visible === 'true';
  }

  /** 器のスキンを選ぶ欄（開くたびに解放状況を読み直す）。 */
  private renderSkins(): void {
    const body = this.modal?.querySelector<HTMLElement>('.modal__body');
    if (!body) return;
    let section = body.querySelector<HTMLElement>('[data-ui="skin-picker"]');
    if (!section) {
      section = document.createElement('div');
      section.className = 'field skin-picker';
      section.dataset.ui = 'skin-picker';
      const tools = body.querySelector('.field--action');
      if (tools) body.insertBefore(section, tools);
      else body.append(section);
    }
    const unlocked = unlockedSkinIds();
    const current = this.context.settings.bowlSkin;
    // 買い切りのお礼の器は、課金のない Web 版・Android 版では出さない
    const skins = BOWL_SKINS.filter((s) => !s.appOnly || this.context.assist.canPurchase);
    const label = document.createElement('span');
    label.className = 'field__label';
    label.textContent = `器をえらぶ（${skins.filter((s) => unlocked.has(s.id)).length} / ${skins.length}）`;
    const list = document.createElement('div');
    list.className = 'skin-picker__list';
    for (const skin of skins) {
      const open = unlocked.has(skin.id);
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'skin-picker__item';
      btn.dataset.selected = String(skin.id === current);
      btn.disabled = !open;
      const swatch = document.createElement('span');
      swatch.className = 'skin-picker__swatch';
      const hex = (n: number) => `#${n.toString(16).padStart(6, '0')}`;
      swatch.style.background = hex(skin.body);
      swatch.style.borderColor = hex(skin.rim ?? 0xf2c14e);
      const name = document.createElement('span');
      name.className = 'skin-picker__name';
      name.textContent = open ? skin.name : `🔒 ${skin.name}`;
      const note = document.createElement('span');
      note.className = 'skin-picker__note';
      note.textContent = open ? (skin.id === current ? '使用中' : '') : `条件：${skin.unlock}`;
      btn.append(swatch, name, note);
      btn.setAttribute(
        'aria-label',
        open ? `${skin.name}${skin.id === current ? '（使用中）' : 'に替える'}` : `${skin.name}（まだ使えません。条件：${skin.unlock}）`,
      );
      btn.addEventListener('click', () => {
        this.update({ bowlSkin: skin.id });
        this.renderSkins();
      });
      list.append(btn);
    }
    section.replaceChildren(label, list);
  }

  /** テスト用ビルドだけ：広告の状態を設定画面の下に出す（2026-10-12 合間の広告が出ない件の切り分け用）。 */
  private renderAdDiagnostics(): void {
    const lines = this.context.assist.adDiagnostics();
    const note = this.modal?.querySelector<HTMLElement>('[data-ui="storage-note"]');
    let box = this.modal?.querySelector<HTMLElement>('[data-ui="ad-debug"]') ?? null;
    if (!lines || !note) {
      box?.remove();
      return;
    }
    if (!box) {
      box = document.createElement('pre');
      box.dataset.ui = 'ad-debug';
      box.style.cssText =
        'white-space:pre-wrap;font-size:11px;line-height:1.5;text-align:left;margin:12px 0 0;padding:8px;border-radius:8px;background:rgba(0,0,0,.06);user-select:text;-webkit-user-select:text;';
      note.after(box);
    }
    box.textContent = ['【広告の状態（テスト用ビルドだけに表示）】', ...lines].join('\n');
  }

  open(): void {
    this.renderSkins();
    this.renderAdDiagnostics();
    if (!this.modal) return;
    this.resumeOnClose = this.context.state.is(GameState.PLAYING);
    if (this.resumeOnClose) this.context.bus.emit('command:pause');
    this.modal.dataset.visible = 'true';
    this.modal.querySelector<HTMLElement>('input, button')?.focus();
  }

  close(): void {
    if (!this.modal) return;
    this.modal.dataset.visible = 'false';
    if (this.resumeOnClose && this.context.state.is(GameState.PAUSED)) {
      this.context.bus.emit('command:resume');
    }
    this.resumeOnClose = false;
  }

  private update(partial: Partial<GameSettings>): void {
    Object.assign(this.context.settings, partial);
    const saved = this.context.storage.updateSettings(partial);
    Object.assign(this.context.settings, saved);
    this.context.audio.setVolumes({
      bgmVolume: this.context.settings.bgmVolume,
      seVolume: this.context.settings.seVolume,
    });
    this.context.bus.emit('settings:change', { settings: this.context.settings });
  }
}
